/**
 * T197 — /expenses/upload — Multi-Upload page (US2 MVP).
 *
 * Drag-&-drop zone via react-dropzone; shows per-file processing phase.
 */
import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useDropzone } from 'react-dropzone';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { Button, PageHeader } from '../components/ui';
import {
  useUploadExpenseFiles,
  useProcessingStatus,
  type UploadedExpense,
} from '../services/api/upload';
import { useToastHelpers } from '../components/ui/Toast';

export const Route = createFileRoute('/expenses_/upload')({
  component: UploadPage,
});

function UploadPage() {
  return (
    <ProtectedRoute>
      <AppShell>
        <UploadInner />
      </AppShell>
    </ProtectedRoute>
  );
}

const PHASE_LABELS: Record<string, string> = {
  queued: 'Warteschlange',
  erechnung_check: 'eRechnung-Check',
  ocr: 'OCR',
  field_extract: 'Felder extrahieren',
  summencheck: 'Summencheck',
  done: 'Fertig',
  failed: 'Fehler',
};

function UploadInner() {
  const [uploaded, setUploaded] = useState<UploadedExpense[]>([]);
  const upload = useUploadExpenseFiles();
  const toast = useToastHelpers();

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/xml': ['.xml'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    onDrop: async (files) => {
      try {
        const res = await upload.mutateAsync(files);
        setUploaded((prev) => [...res.items, ...prev]);
        toast.success(`${res.items.length} Datei(en) im Upload`);
      } catch (e) {
        toast.error(`Upload fehlgeschlagen: ${(e as Error).message}`);
      }
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Belege per Drag & Drop"
        subtitle="PDF, XML (XRechnung), JPG, PNG — bis zu 200 Dateien auf einmal"
        actions={
          <Link to="/expenses">
            <Button variant="ghost">Zur Liste</Button>
          </Link>
        }
      />

      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-12 text-center transition cursor-pointer ${
          isDragActive
            ? 'border-white/40 bg-white/5'
            : 'border-white/10 hover:border-white/20'
        }`}
      >
        <input {...getInputProps()} />
        <p className="text-white/70 text-lg">
          {isDragActive ? 'Dateien hier loslassen…' : 'Dateien hier ablegen oder klicken'}
        </p>
        <p className="text-white/40 text-sm mt-2">
          eRechnung wird automatisch erkannt und direkt validiert.
        </p>
      </div>

      {uploaded.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-lg font-medium text-white">Verarbeitung</h2>
          {uploaded.map((u) => (
            <UploadRow key={u.expenseId} item={u} />
          ))}
        </div>
      )}
    </div>
  );
}

function UploadRow({ item }: { item: UploadedExpense }) {
  const { data } = useProcessingStatus(item.expenseId);
  const phase = data?.phase ?? 'queued';
  const done = phase === 'done' || phase === 'failed';

  return (
    <div className="flex items-center justify-between p-3 bg-white/5 border border-white/10 rounded-lg">
      <div>
        <div className="text-white font-medium">{item.fileName}</div>
        <div className="text-white/50 text-sm">
          {PHASE_LABELS[phase] ?? phase}
          {data?.errorMessage ? ` — ${data.errorMessage}` : ''}
        </div>
      </div>
      <div className="flex items-center gap-3">
        {!done && (
          <span className="inline-block w-3 h-3 rounded-full bg-blue-400 animate-pulse" />
        )}
        <Link
          to="/expenses/$id"
          params={{ id: item.expenseId }}
          className="text-sm text-blue-300 hover:underline"
        >
          Öffnen →
        </Link>
      </div>
    </div>
  );
}
